import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { Subject } from "rxjs";
import { ProjectStatus } from "../enum/project-status.enum";
import {
  FilterDisplayObject,
  FilterObject,
} from "../interfaces/filter-object.interface";
import { TableFilter } from "../interfaces/table-filter.interface";

@Injectable({
  providedIn: "root",
})
export class UtilService {
  refreshBreadcrumb: Subject<void> = new Subject();
  refreshTimeTracker: Subject<void> = new Subject();

  constructor() {}

  triggerBreadcrumbRefresh(): void {
    this.refreshBreadcrumb.next();
  }

  triggerTimeTrackerRefresh(): void {
    this.refreshTimeTracker.next();
  }

  objectToHttpParams(obj: Record<string, any>): HttpParams {
    let params = new HttpParams();

    Object.entries(obj).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        if (Array.isArray(value)) {
          // Handle arrays - append multiple values
          if (key === "sort") {
            value.forEach((el) => {
              params = params.append(key, el);
            });
          } else {
            params = params.set(key, value.join(","));
          }
          // TODO CHECK IF IT CAUSES ISSUES. WILL REDO TO JOIN ELEMENTS
          // value.forEach((item) => {
          //   if (item !== null && item !== undefined) {
          //     params = params.append(key, item.toString());
          //   }
          // });
        } else if (typeof value === "object") {
          // Handle nested objects (flatten or stringify)
          params = params.set(key, JSON.stringify(value));
        } else {
          params = params.set(key, value.toString());
        }
      }
    });

    return params;
  }

  convertFilterObjectToString(filterObject: FilterObject): string {
    if (!filterObject) {
      return "";
    }
    return Object.entries(filterObject)
      .filter(([_, value]) => value != null && value !== "")
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          const cleanArray = value.filter((v) => v != null && v !== "");
          if (cleanArray.length === 0) return null;

          // Handle date ranges - convert [startDate, endDate] to separate params
          if (
            key === "createdAt" ||
            key === "updatedAt" ||
            key.includes("Date")
          ) {
            const [startDate, endDate] = cleanArray;
            let baseKey = "";
            if (key === "updatedAt") {
              baseKey = "updated";
            } else {
              baseKey = "created";
            }
            const dateParams = [];

            if (startDate) {
              const formattedStart = this.getLocalDateString(startDate);
              dateParams.push(
                `${baseKey}After=${encodeURIComponent(formattedStart)}`,
              );
            }

            if (endDate) {
              const formattedEnd = this.getLocalDateString(endDate);
              dateParams.push(
                `${baseKey}Before=${encodeURIComponent(formattedEnd)}`,
              );
            }

            return dateParams.join("&");
          }

          // Handle arrays (roles, etc.)
          return `${key}=${cleanArray.join(",")}`;
        }

        // Handle single values
        return `${key}=${encodeURIComponent(value)}`;
      })
      .filter(Boolean)
      .join("&");
  }

  convertFilterDateRangeToStartEndDates(
    filterObject: { [key: string]: any },
    dateRangeKey: string,
    startDateKey: string,
    endDateKey: string,
  ): { [key: string]: any } {
    if (filterObject[dateRangeKey]) {
      const [startDate, endDate] = filterObject[dateRangeKey];
      if (startDate) {
        filterObject[startDateKey] = this.getLocalDateString(startDate);
      }
      if (endDate) {
        filterObject[endDateKey] = this.getLocalDateString(endDate);
      }
      delete filterObject[dateRangeKey];
    }
    return filterObject;
  }

  /**
   * Converts filter object to display-friendly array of filter descriptions
   * @param filters - Object containing filter values
   * @returns Array of readable filter strings
   */
  convertFiltersToDisplayArray(
    filters: FilterObject,
    filterDef: TableFilter[],
  ): FilterDisplayObject[] {
    if (!filters) {
      return [];
    }
    const displayFilters: FilterDisplayObject[] = [];
    Object.entries(filters).forEach(([key, value]) => {
      if (value == null || value === "") return;

      const filterDefObject = filterDef.find((def) => def.field === key);
      const fieldLabel =
        filterDefObject?.fieldLabel || this.capitalizeFirst(key);

      if (Array.isArray(value)) {
        const cleanArray = value.filter((v) => v != null && v !== "");
        if (cleanArray.length === 0) return;

        // Handle date ranges
        if (
          key === "createdAt" ||
          key === "updatedAt" ||
          key.includes("Date") ||
          key === "invoiceMonth"
        ) {
          const [startDate, endDate] = cleanArray;
          if (startDate && endDate) {
            const start = this.getLocalDateString(startDate);
            const end = this.getLocalDateString(endDate);
            displayFilters.push({
              filterKey: key,
              displayString: `${fieldLabel}: ${start} - ${end}`,
            });
          } else if (startDate) {
            displayFilters.push({
              filterKey: key,
              displayString: `${fieldLabel}: ${this.getLocalDateString(startDate)}`,
            });
          } else if (endDate) {
            displayFilters.push({
              filterKey: key,
              displayString: `${fieldLabel}: ${this.getLocalDateString(endDate)}`,
            });
          }
          return;
        }

        // Handle arrays (roles, etc.)
        if (key === "role") {
          const formattedRoles = cleanArray.map(
            (role) =>
              filterDefObject?.selectOptions?.find((r) => r.value === role)
                ?.label || this.capitalizeFirst(role),
          );
          displayFilters.push({
            filterKey: key,
            displayString: `${fieldLabel}: ${formattedRoles.join(", ")}`,
          });
        } else {
          displayFilters.push({
            filterKey: key,
            displayString: `${fieldLabel}: ${cleanArray.join(", ")}`,
          });
        }
      } else {
        // Handle single values
        if (key === "role") {
          const formattedRole =
            filterDefObject?.selectOptions?.find((r) => r.value === value)
              ?.label || this.capitalizeFirst(value as string);
          displayFilters.push({
            filterKey: key,
            displayString: `${fieldLabel}: ${formattedRole}`,
          });
        } else {
          // For select/lazySelect filters, resolve the value to its option label.
          const optionLabel = filterDefObject?.selectOptions?.find(
            (o) => o.value === value,
          )?.label;
          displayFilters.push({
            filterKey: key,
            displayString: `${fieldLabel}: ${optionLabel ?? value}`,
          });
        }
      }
    });

    return displayFilters;
  }

  getInitials(name: string): string {
    if (!name) return "";
    const words = name.trim().split(" ");

    if (words.length === 1) {
      return words[0][0].toUpperCase();
    }

    const first = words[0][0];
    const last = words[words.length - 1][0];

    return (first + last).toUpperCase();
  }

  convertIsoStringDateToReadable(
    isoDate: string,
    tLoco: TranslocoService,
  ): string {
    const date = new Date(isoDate);
    const now = new Date();

    // Helper to check if same day
    const isSameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    // Create a date for "yesterday"
    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);

    const time = date.toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });

    if (isSameDay(date, now)) {
      return `${tLoco.translate("general.today")}, ${time}`;
    }

    if (isSameDay(date, yesterday)) {
      return `${tLoco.translate("general.yesterday")}, ${time}`;
    }

    return date.toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  getProjectStatusTagSeverity(status: ProjectStatus): string {
    switch (status) {
      case ProjectStatus.ACTIVE:
        return "primary";
      case ProjectStatus.CANCELLED:
        return "danger";
      case ProjectStatus.PROPOSAL:
        return "info";
      default:
        return "secondary";
    }
  }

  jsonToFormData(json: Record<string, any>): FormData {
    const formData = new FormData();

    Object.keys(json).forEach((key) => {
      const value = json[key];

      if (value === null || value === undefined) return;

      if (value instanceof File) {
        formData.append(key, value);
      } else {
        formData.append(key, value.toString());
      }
    });

    return formData;
  }

  /**
   * Extracts date string in local timezone (avoids UTC conversion issues)
   * @param dateInput - Date string or Date object
   * @returns YYYY-MM-DD format in local timezone
   */
  getLocalDateString(dateInput: string | Date): string {
    const date =
      typeof dateInput === "string" ? new Date(dateInput) : dateInput;

    // Use local timezone methods to avoid UTC conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  /**
   * Parse UNTIL date from RRule format (YYYYMMDDTHHMMSSZ)
   */
  parseRRuleUntilDate(dateString: string): Date {
    // Format: 20250315T235959Z or 20250315
    const cleaned = dateString.replace(/[^0-9]/g, "");

    const year = parseInt(cleaned.substring(0, 4), 10);
    const month = parseInt(cleaned.substring(4, 6), 10) - 1; // 0-indexed
    const day = parseInt(cleaned.substring(6, 8), 10);

    let hours = 0;
    let minutes = 0;
    let seconds = 0;

    if (cleaned.length >= 14) {
      hours = parseInt(cleaned.substring(8, 10), 10);
      minutes = parseInt(cleaned.substring(10, 12), 10);
      seconds = parseInt(cleaned.substring(12, 14), 10);
    }

    // RRULE dates are typically in UTC
    return new Date(Date.UTC(year, month, day, hours, minutes, seconds));
  }

  /**
   * Capitalizes first letter of a string
   */
  private capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }
}
